<script>
import {
  GlLink,
  GlDisclosureDropdown,
  GlDisclosureDropdownGroup,
  GlDisclosureDropdownItem,
} from '@gitlab/ui';
import Tracking from '~/javascripts/tracking';
import {
  TRACKING_ACTIONS,
  TRACKING_LABELS,
  TRACKING_CATEGORIES,
} from '~/javascripts/tracking/constants';
import { __ } from '~/javascripts/lib/locale';
import { SIGN_IN_PATH } from '~/javascripts/constants';
import { SUBMIT_FEEDBACK_URL, LICENSING_FAQ_URL, DOCUMENTATION_URL } from '../constants';

export default {
  name: 'NavBarItems',
  components: {
    GlLink,
    GlDisclosureDropdown,
    GlDisclosureDropdownGroup,
    GlDisclosureDropdownItem,
  },
  inject: {
    isSignedIn: 'isSignedIn',
    hasSubscriptions: 'hasSubscriptions',
    customerFullName: {
      default: '',
    },
    hasBillingAccount: {
      default: false,
    },
    contactUsPath: 'contactUsPath',
    subscriptionsPath: 'subscriptionsPath',
    invoicesPath: 'invoicesPath',
    accountDetailsPath: 'accountDetailsPath',
    paymentMethodsPath: 'paymentMethodsPath',
    signOutPath: 'signOutPath',
  },
  computed: {
    jhMode() {
      return window.gon.global?.jh;
    },
  },
  created() {
    this.navClasses = {
      'navbar-nav': true,
      'gl-ml-auto': true,
      'gl-flex-row': this.hasBillingAccount, // Always display the menu inline for users with a billing account
    };
    this.navLinkClasses = {
      // GlLink and GlButton[variant="link"] overrides
      '!gl-justify-start': true,
      'gl-grow': true,
      '!gl-text-sm': true,
      'gl-font-bold': true,
      '!gl-text-brand-gray-03': true,
      '!gl-no-underline': true,
      'active:!gl-text-brand-gray-05': true,
      'focus:!gl-text-brand-gray-05': true,
      'hover:!gl-text-brand-gray-05': true,
      '!gl-py-3': true,
      // Padding x is only needed when displayed in the menu bar, i.e. not in the toggle menu
      // When user has a billing account we never display the items in a toggle menu
      'lg:!gl-px-3': !this.hasBillingAccount,
      '!gl-px-3': this.hasBillingAccount,
    };
  },
  methods: {
    trackEvent(label) {
      const tracking = new Tracking();
      tracking.event(TRACKING_CATEGORIES.webstore, TRACKING_ACTIONS.clickLink, { label });
    },
  },
  i18n: {
    managePurchases: __('Manage purchases'),
    viewInvoices: __('View invoices'),
    documentation: __('Documentation'),
    licensingFaq: __('Licensing FAQ'),
    contactUs: __('Contact us'),
    submitFeedback: __('Submit feedback'),
    myProfile: __('My profile'),
    myBillingAccount: __('My billing account'),
    profileSettings: __('Profile settings'),
    paymentMethods: __('Payment methods'),
    faq: __('Frequently asked questions'),
    signOut: __('Sign out'),
    signIn: __('Sign in'),
  },
  SUBMIT_FEEDBACK_URL,
  LICENSING_FAQ_URL,
  DOCUMENTATION_URL,
  TRACKING_LABELS,
  SIGN_IN_PATH,
  navLinkAttributes: {
    role: 'menuitem',
  },
  navItemClasses: {
    'gl-flex': true,
  },
  dropdownClasses: {
    'gl-grow': true,
  },
};
</script>

<template>
  <ul :class="navClasses" role="menubar">
    <template v-if="isSignedIn">
      <li :class="$options.navItemClasses">
        <gl-link
          v-if="!hasBillingAccount"
          :href="subscriptionsPath"
          :class="navLinkClasses"
          v-bind="$options.navLinkAttributes"
          >{{ $options.i18n.managePurchases }}</gl-link
        >
      </li>
      <li :class="$options.navItemClasses">
        <gl-link
          v-if="!hasBillingAccount && !jhMode"
          :href="invoicesPath"
          :class="navLinkClasses"
          v-bind="$options.navLinkAttributes"
          >{{ $options.i18n.viewInvoices }}</gl-link
        >
      </li>
      <li :class="$options.navItemClasses">
        <gl-disclosure-dropdown
          v-if="!jhMode"
          data-testid="info"
          icon="question"
          variant="link"
          :class="$options.dropdownClasses"
          :toggle-class="navLinkClasses"
          @shown="trackEvent($options.TRACKING_LABELS.headerInfo)"
          @hidden="trackEvent($options.TRACKING_LABELS.headerInfo)"
        >
          <gl-disclosure-dropdown-group>
            <gl-disclosure-dropdown-item
              :item="{
                href: $options.DOCUMENTATION_URL,
                text: $options.i18n.documentation,
                extraAttrs: $options.navLinkAttributes,
              }"
            />
            <gl-disclosure-dropdown-item
              :item="{
                href: $options.LICENSING_FAQ_URL,
                text: $options.i18n.faq,
                extraAttrs: $options.navLinkAttributes,
              }"
            />
            <gl-disclosure-dropdown-item
              :item="{
                href: $options.CONTACT_SUPPORT_URL,
                text: $options.i18n.contactUs,
                extraAttrs: $options.navLinkAttributes,
              }"
            />
          </gl-disclosure-dropdown-group>

          <gl-disclosure-dropdown-group bordered>
            <gl-disclosure-dropdown-item
              :item="{
                href: $options.SUBMIT_FEEDBACK_URL,
                text: $options.i18n.submitFeedback,
                extraAttrs: $options.navLinkAttributes,
              }"
            />
          </gl-disclosure-dropdown-group>
        </gl-disclosure-dropdown>
      </li>
      <li :class="$options.navItemClasses">
        <gl-disclosure-dropdown
          data-testid="profile"
          :toggle-text="$options.i18n.myProfile"
          variant="link"
          :class="$options.dropdownClasses"
          :toggle-class="navLinkClasses"
          @shown="trackEvent($options.TRACKING_LABELS.headerProfile)"
          @hidden="trackEvent($options.TRACKING_LABELS.headerProfile)"
        >
          <gl-disclosure-dropdown-group>
            <template #group-label>
              {{ customerFullName }}
            </template>

            <gl-disclosure-dropdown-item
              v-if="hasBillingAccount"
              :item="{
                href: '/',
                text: $options.i18n.myBillingAccount,
                extraAttrs: $options.navLinkAttributes,
              }"
            />
            <gl-disclosure-dropdown-item
              :item="{
                href: accountDetailsPath,
                text: $options.i18n.profileSettings,
                extraAttrs: $options.navLinkAttributes,
              }"
            />
            <gl-disclosure-dropdown-item
              v-if="!hasBillingAccount && hasSubscriptions"
              :item="{
                href: paymentMethodsPath,
                text: $options.i18n.paymentMethods,
                extraAttrs: $options.navLinkAttributes,
              }"
            />
          </gl-disclosure-dropdown-group>
          <gl-disclosure-dropdown-group bordered>
            <gl-disclosure-dropdown-item
              :item="{
                href: signOutPath,
                text: $options.i18n.signOut,
                extraAttrs: { ...$options.navLinkAttributes, 'data-method': 'delete' },
              }"
            />
          </gl-disclosure-dropdown-group>
        </gl-disclosure-dropdown>
      </li>
    </template>
    <template v-else>
      <li :class="$options.navItemClasses">
        <gl-link
          data-testid="navbar-sign-in"
          :href="$options.SIGN_IN_PATH"
          :class="navLinkClasses"
          v-bind="$options.navLinkAttributes"
          >{{ $options.i18n.signIn }}</gl-link
        >
      </li>
    </template>
  </ul>
</template>
